import React, { useState } from 'react';
import { BookOpen, CheckCircle, ArrowLeft, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

const StorybookSubscribe = () => {
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    organization: '',
    role: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      toast.error('Please enter your email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/storybook/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setIsSuccess(true);
        toast.success('Successfully subscribed!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to subscribe');
      }
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main id="main-content" tabIndex={-1} className="py-20">
          <div className="max-w-xl mx-auto px-4">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <div className="w-20 h-20 bg-[#95C73E]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-[#95C73E]" />
              </div>
              <h1 className="text-2xl font-bold text-[#0A2037] mb-4">You're Subscribed!</h1>
              <p className="text-slate-600 mb-6">
                Thank you for subscribing to the DCELC Educator Storybook. You'll be among the first 
                to receive it when it's ready. Check your email for a confirmation.
              </p>
              <div className="bg-[#079897]/10 rounded-xl p-4 mb-6">
                <p className="text-[#079897] font-medium">
                  "Early childhood educators have powerful stories to tell."
                </p>
              </div>
              <Link to="/">
                <Button className="bg-[#079897] hover:bg-[#067a79]">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Return to Homepage
                </Button>
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main id="main-content" tabIndex={-1} className="py-16">
        <div className="max-w-2xl mx-auto px-4">
          {/* Back Link */}
          <Link to="/" className="inline-flex items-center text-[#079897] hover:text-[#067a79] mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Homepage
          </Link>

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#95C73E] to-[#079897] p-8 text-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                  <BookOpen className="w-7 h-7" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold">Educator Storybook</h1>
                  <p className="text-white/80">Subscribe to be the first to receive it</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-8">
              <div className="mb-8">
                <p className="text-slate-600 leading-relaxed">
                  Early childhood educators have powerful stories to tell about their work, their dedication, 
                  and the challenges they face. Our upcoming Educator Storybook will help elevate the voices 
                  of the workforce and build public understanding of why pay equity matters.
                </p>
                <p className="text-slate-600 leading-relaxed mt-4">
                  <strong>Subscribe below</strong> to be among the first to receive the storybook when it's released.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      type="text"
                      value={formData.first_name}
                      onChange={handleChange}
                      placeholder="Enter your first name"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      type="text"
                      value={formData.last_name}
                      onChange={handleChange}
                      placeholder="Enter your last name"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email Address <span className="text-red-500">*</span></Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email address"
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="organization">Organization (Optional)</Label>
                  <Input
                    id="organization"
                    name="organization"
                    type="text"
                    value={formData.organization}
                    onChange={handleChange}
                    placeholder="e.g., Child care center name, school, etc."
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="role">Your Role (Optional)</Label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="w-full mt-1 rounded-md border border-slate-200 px-3 py-2 text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#079897]"
                  >
                    <option value="">Select your role...</option>
                    <option value="Early Childhood Educator">Early Childhood Educator</option>
                    <option value="Center Director">Center Director</option>
                    <option value="Program Administrator">Program Administrator</option>
                    <option value="Parent/Family Member">Parent/Family Member</option>
                    <option value="Policy Advocate">Policy Advocate</option>
                    <option value="Community Partner">Community Partner</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#079897] hover:bg-[#067a79] py-6 text-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    'Subscribing...'
                  ) : (
                    <>
                      <Heart className="w-5 h-5 mr-2" />
                      Subscribe Now
                    </>
                  )}
                </Button>
              </form>

              {/* Privacy Note */}
              <p className="text-xs text-slate-500 text-center mt-6">
                We respect your privacy. Your information will only be used to send you the Educator Storybook 
                and related updates from DCELC. You can unsubscribe at any time.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StorybookSubscribe;
